// One agent's report card — the same component whether an agent opens their own
// ("Gelişimim") or a QMS/supervisor opens someone else's from a table. The API decides
// what they may see; this only decides the wording.

import * as api from '../api';
import type { Meta, PanelUser } from '../types';
import { useAsync, usePeriodPicker } from '../hooks';
import type { PeriodPicker } from '../hooks';
import { Callout, CriterionBar, Empty, Kpi, Loading, Note, PageHead, Panel, Pill, ScorePill } from '../ui';
import { STATUS_LABELS, STATUS_TONE, delta10, score10, shortDateTime } from '../format';
import { navigate } from '../App';
import AgentOfWeek from './AgentOfWeek';
import PeriodBar from './PeriodBar';

export default function AgentReport({ agentId, user, meta, picker: outerPicker, embedded }: {
  agentId: number | null; user: PanelUser; meta: Meta | null;
  /** When a parent screen owns the period bar (Raporlar → Temsilci Raporu), it passes its
   *  own picker in so both halves of that page move together. */
  picker?: PeriodPicker;
  /** Rendered inside another screen: drop the page heading, which that screen already has. */
  embedded?: boolean;
}) {
  // The hook runs unconditionally (rules of hooks); its value is simply unused when the
  // parent supplied a picker.
  const ownPicker = usePeriodPicker(meta);
  const picker = outerPicker ?? ownPicker;
  // null = "me" — the server resolves it from the session, so an agent's own card needs no
  // id in the client at all.
  const targetId: number | 'me' = agentId ?? 'me';
  const state = useAsync(
    () => api.getAgentReport(targetId, picker.id),
    [targetId, picker.id],
  );
  const isSelf = user.role === 'agent';

  // The greeting lives on the agent's HOME (MyDashboard). This screen is the period report,
  // and titling it "Merhaba X 👋" too was half of why the two nav entries looked identical.
  const title = state.data && !isSelf ? state.data.agent.displayName : (isSelf ? 'Gelişimim' : 'Agent raporu');
  const head = embedded ? null : (
    <PageHead
      icon="workspace_premium"
      title={title}
      subtitle={state.data ? (
        <>
          {state.data.period.label} · {state.data.agent.brandName}
          {isSelf ? ' · kalite performansın ve QMS ekibinden gelen geri bildirimler' : ''}
        </>
      ) : ' '}
      right={<PeriodBar picker={picker} />}
    />
  );

  if (!picker.id && !state.data) return <>{head}<Loading /></>;
  if (state.error) return <>{head}<Note error>{state.error}</Note></>;
  if (!state.data) return <>{head}<Loading /></>;

  const r = state.data;
  const deltaText = delta10(r.summary.avgScore, r.previous.avgScore);
  const deltaKind = r.summary.avgScore !== null && r.previous.avgScore !== null
    ? (r.summary.avgScore >= r.previous.avgScore ? 'up' : 'down')
    : 'flat';
  const weakest = [...r.criteria].filter((c) => c.avg !== null)
    .sort((a, b) => (a.avg as number) - (b.avg as number))[0];

  return (
    <>
      {head}

      <div className="kpis">
        <Kpi
          label="Genel kalite ortalaması" value={score10(r.summary.avgScore)} unit="/10"
          icon="workspace_premium" tone="good"
          delta={deltaText ? `${deltaText} · önceki döneme göre` : 'karşılaştırılacak dönem yok'}
          deltaKind={deltaText ? deltaKind : 'flat'}
        />
        <Kpi
          label="Bu dönem değerlendirme" value={r.summary.evalCount}
          icon="fact_check" tone="accent"
          delta={`önceki dönem: ${r.previous.evalCount}`} deltaKind="flat"
        />
        <Kpi
          label="Kritik hata" value={r.summary.criticalCount}
          icon={r.summary.criticalCount ? 'warning' : 'shield'}
          tone={r.summary.criticalCount ? 'bad' : 'good'}
          delta={r.summary.criticalCount ? 'her biri o çağrıyı 0 yaptı' : 'temiz dönem · tebrikler'}
          deltaKind={r.summary.criticalCount ? 'down' : 'up'}
        />
        <Kpi
          label="Ekip sıralaması"
          value={r.standing?.rank ?? '—'} unit={r.standing ? `/${r.standing.of}` : undefined}
          icon="leaderboard" tone="warn"
          delta={`${r.agent.brandName} ekibi içinde`} deltaKind="flat"
        />
      </div>

      {/* The agent's own Gelişimim only (#298/#299): Genel Bakış's live board, their brand, their row
          marked. Staff opening someone's card already have it on Genel Bakış. */}
      {isSelf ? <AgentOfWeek period={picker.id} brand="" forAgent /> : null}

      <div className="grid-2 narrow">
        <Panel
          icon="history"
          title={isSelf ? 'Değerlendirmelerim' : 'Değerlendirmeler'}
          hint={r.period.label}
          right={isSelf ? <button className="link" type="button" onClick={() => navigate('evaluations')}>Tümü</button> : undefined}
        >
          {r.evaluations.length === 0 ? (
            <Empty>Bu dönemde değerlendirme yok.</Empty>
          ) : (
            <div className="tbl-wrap">
              <table>
                <thead>
                  <tr>
                    <th>Tarih</th><th>Liste</th><th>Kalite</th>
                    <th>Değerlendiren</th><th>Durum</th><th />
                  </tr>
                </thead>
                <tbody>
                  {r.evaluations.map((e) => (
                    <tr key={e.id} className={e.hasCritical ? 'crit' : undefined}>
                      <td className="tnum">{shortDateTime(e.callAt)}</td>
                      <td>
                        <span className="who" style={{ display: 'block' }}>
                          <span className="nm">{e.listName || '—'}</span>
                          <span className="sub tnum" translate="no">{[e.playerUserId != null ? `#${e.playerUserId}` : '', e.callType !== e.listName ? e.callType : ''].filter(Boolean).join(' · ') || `#${e.id}`}</span>
                        </span>
                      </td>
                      <td><ScorePill score={e.finalScore} hasCritical={e.hasCritical} /></td>
                      <td>{e.evaluatorName}</td>
                      <td><Pill tone={STATUS_TONE[e.status]}>{STATUS_LABELS[e.status]}</Pill></td>
                      <td className="r">
                        <button className="link" type="button" onClick={() => navigate(`evaluation/${e.id}`)}>
                          Detay
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Panel>

        <div className="stack">
          <Panel icon="insights" title={isSelf ? 'Kriter kırılımım' : 'Kriter kırılımı'} hint={r.period.label}>
            <div className="panel-body tight">
              {r.criteria.map((c) => (
                <CriterionBar key={c.key} label={c.label} value={c.avg} weight={c.weight} />
              ))}
              <div style={{ marginTop: 12 }}>
                {weakest ? (
                  <Callout tone="warn" icon="school">
                    <b>Odak: {weakest.label}</b>
                    <br />
                    {`${score10(weakest.avg)}/10 · ağırlığı %${weakest.weight} · bu kriterdeki bir puan genel ortalamayı en çok etkileyen alanlardan.`}
                  </Callout>
                ) : (
                  <Callout>Bu dönemde puanlanmış kriter yok.</Callout>
                )}
              </div>
            </div>
          </Panel>

          {r.criticals.length > 0 ? (
            <Panel icon="error" tone="bad" title="Kritik hatalar" hint={r.period.label}>
              <div className="critlist">
                {r.criticals.map((c) => (
                  <div key={c.key} className="critline">
                    <span className="tag bad">×{c.count}</span>
                    <span className="grow">{c.label}</span>
                  </div>
                ))}
              </div>
            </Panel>
          ) : null}
        </div>
      </div>
    </>
  );
}
